import { Link } from 'react-router-dom'
import type { OrderListItemDTO } from '@wf/shared'
import { stampErrorText } from '../attendance/stampError'
import { formatHMS } from '../time/format'
import { useOrderTime } from '../time/useOrderTime'
import { useWorkTimer } from '../time/useWorkTimer'

const STATUS_MODIFIER: Record<string, string> = {
  offen: 'wf-jobcard--offen',
  arbeit: 'wf-jobcard--arbeit',
  wartet: 'wf-jobcard--wartet',
  fertig: 'wf-jobcard--fertig',
  abgeholt: 'wf-jobcard--abgeholt',
}

// Eigene, handlungsorientierte Status-Beschriftung der „Meine Liste" (wie im Alt-Monolithen): sagt dem
// Mechaniker, was als Nächstes dran ist — nicht der nüchterne Board-Status.
const MYJOB_STATUS_LABEL: Record<string, string> = {
  offen: 'Als Nächstes',
  arbeit: 'Läuft',
  wartet: 'Wartet auf Teil',
  fertig: 'Fertig',
  abgeholt: 'Abgeholt',
}

/**
 * Eine große Karte der „Meine Liste" (Port der v6-bigCard): Kennzeichen, Status, Fahrzeug, Notiz,
 * Aufgaben-Fortschritt und die gebuchte/laufende Zeit — rechts der prominente Start/Stopp-Knopf. Der
 * Karten-Körper verlinkt aufs Detail; der Timer-Knopf ist sein Geschwister (kein <button> im <a>).
 *
 * Zeit-Logik aus dem geteilten useWorkTimer (persistenter Lauf-Slot, Buchung über die Offline-Outbox) —
 * gleiche Wahrheit wie der OrderTimer im Detail, nur in den bigCard-Look integriert.
 */
export function MyJobCard({ order }: { order: OrderListItemDTO }) {
  const timer = useWorkTimer(order.id)
  const time = useOrderTime(order.id)
  const booked = time.data?.totalSeconds ?? order.timeSeconds
  const liveTotal = booked + (timer.running ? timer.elapsed : 0)
  const showTime = timer.running || booked > 0

  return (
    <article className={`wf-jobcard ${STATUS_MODIFIER[order.status] ?? ''}`.trim()}>
      <Link to={`/orders/${order.id}`} className="wf-jobcard__main" aria-label={`Auftrag ${order.nr ?? order.id} öffnen`}>
        <div className="wf-jobcard__head">
          <span className="wf-plate wf-plate--lg">
            <span className="wf-plate__eu">D</span>
            <span className="wf-plate__nr">{order.plate ?? '—'}</span>
          </span>
          <span className={`wf-pill wf-pill--${order.status}`}>{MYJOB_STATUS_LABEL[order.status] ?? order.status}</span>
        </div>

        <div className="wf-jobcard__car">{order.vehicleModel ?? 'Fahrzeug'}</div>
        {order.note ? <div className="wf-jobcard__note">{order.note}</div> : null}

        <div className="wf-jobcard__facts">
          {order.taskTotal > 0 ? (
            <>
              <progress
                className="wf-card__progress wf-jobcard__bar"
                value={order.taskDone}
                max={order.taskTotal}
                aria-label="Aufgaben-Fortschritt"
              />
              <span className="wf-jobcard__count">
                {order.taskDone} / {order.taskTotal}
              </span>
            </>
          ) : (
            <span className="wf-jobcard__count wf-jobcard__count--none">keine Aufgaben</span>
          )}
          {showTime ? (
            timer.running ? (
              <span className="wf-jobcard__time wf-jobcard__time--live" role="timer" aria-label="laufende Zeit">
                <span className="wf-jobcard__pulse" aria-hidden="true" />
                {formatHMS(liveTotal)}
              </span>
            ) : (
              <span className="wf-jobcard__time wf-mono">{formatHMS(booked)}</span>
            )
          ) : null}
        </div>
      </Link>

      <div className="wf-jobcard__action">
        {order.status === 'wartet' ? (
          <button type="button" className="wf-btn wf-btn--xl wf-btn--wait" disabled>
            Wartet
          </button>
        ) : timer.running ? (
          <button type="button" className="wf-btn wf-btn--xl wf-btn--stop" onClick={timer.stop} disabled={timer.submitting}>
            Stopp
          </button>
        ) : timer.busyOtherOrderId != null ? (
          <button
            type="button"
            className="wf-btn wf-btn--xl wf-btn--start"
            disabled
            title={`Ein Timer läuft bereits für Auftrag #${timer.busyOtherOrderId}`}
          >
            Starten
          </button>
        ) : timer.attBlocked ? (
          <button
            type="button"
            className="wf-btn wf-btn--xl wf-btn--start"
            onClick={timer.startWithStamp}
            disabled={timer.stamping}
            title={
              timer.attBlocked === 'pause'
                ? 'Du bist in der Pause — dieser Start beendet zuerst deine Pause'
                : 'Du bist nicht eingestempelt — dieser Start stempelt dich zuerst ein'
            }
          >
            {timer.attBlocked === 'pause' ? 'Pause beenden + Start' : 'Stempeln + Start'}
          </button>
        ) : (
          <button type="button" className="wf-btn wf-btn--xl wf-btn--start" onClick={timer.start}>
            Starten
          </button>
        )}
      </div>

      {timer.lastResult === 'queued' ? (
        <p className="wf-jobcard__hint" role="status">
          Zeit gesichert – wird automatisch nachgebucht, sobald die Verbindung steht.
        </p>
      ) : null}
      {timer.error ? (
        <p className="wf-jobcard__hint wf-jobcard__hint--err" role="alert">
          Zeit konnte nicht gebucht werden.
        </p>
      ) : null}
      {timer.stampError ? (
        <p className="wf-jobcard__hint wf-jobcard__hint--err" role="alert">
          Einstempeln fehlgeschlagen: {stampErrorText(timer.stampError)}
        </p>
      ) : null}
    </article>
  )
}
